import { forceUpdate, h, Host } from "@stencil/core";
import { insertIntoIndex, removeElement } from "../../common/array";
import { getControlRegisterProperty } from "../../common/registry-properties";
import { KEY_CODES, SCROLLABLE_CLASS, TAB_PARTS_DICTIONARY } from "../../common/reserved-names";
import { adoptCommonThemes } from "../../common/theme";
import { inBetween, isPseudoElementImg, isRTL, tokenMap, updateDirectionInImageCustomVar } from "../../common/utils";
import { focusComposedPath, MouseEventButton, MouseEventButtons } from "../common/helpers";
import { DEFAULT_TAB_LIST_POSITION, DRAG_PREVIEW, DRAG_PREVIEW_INSIDE_BLOCK, DRAG_PREVIEW_INSIDE_INLINE, DRAG_PREVIEW_OUTSIDE, isBlockDirection, isStartDirection, PANEL_ID } from "./utils";
const TAB_BUTTON_CLASS = "tab";
const CLOSE_BUTTON_CLASS = "close-button";
// Custom vars
const TRANSITION_DURATION = "--ch-tab-transition-duration";
const BUTTON_POSITION_X = "--ch-tab-button-position-x";
const BUTTON_POSITION_Y = "--ch-tab-button-position-y";
const BUTTON_SIZE = "--ch-tab-button-size";
const MOUSE_OFFSET_X = "--ch-tab-mouse-offset-x";
const MOUSE_OFFSET_Y = "--ch-tab-mouse-offset-y";
const MOUSE_POSITION_X = "--ch-tab-mouse-position-x";
const MOUSE_POSITION_Y = "--ch-tab-mouse-position-y";
const TAB_LIST_EDGE_START_POSITION = "--ch-tab-list-start";
const TAB_LIST_EDGE_END_POSITION = "--ch-tab-list-end";
// Selectors
const FIRST_CAPTION_BUTTON = (tabListRef) => tabListRef.querySelector(":scope>button");
const LAST_CAPTION_BUTTON = (tabListRef) => tabListRef.querySelector(":scope>button:last-child");
// Utility functions
const setProperty = (element, property, value) => element.style.setProperty(property, `${value}px`);
const setButtonInitialPosition = (element, positionX, positionY) => {
    setProperty(element, BUTTON_POSITION_X, positionX);
    setProperty(element, BUTTON_POSITION_Y, positionY);
};
const setButtonSize = (element, size) => {
    setProperty(element, BUTTON_SIZE, size);
};
const setMousePosition = (element, positionX, positionY) => {
    setProperty(element, MOUSE_POSITION_X, positionX);
    setProperty(element, MOUSE_POSITION_Y, positionY);
};
// Useful to implement snap to the edges
const setTabListStartEndPosition = (element, startPosition, endPosition) => {
    setProperty(element, TAB_LIST_EDGE_START_POSITION, startPosition);
    setProperty(element, TAB_LIST_EDGE_END_POSITION, endPosition);
};
const getTabListSizesAndSetPosition = (hostRef, tabListRef, blockDirection, buttonRect) => {
    const tabListRect = tabListRef.getBoundingClientRect();
    // Tab List information
    const tabListSizes = {
        xStart: tabListRect.x,
        xEnd: tabListRect.x + tabListRect.width,
        yStart: tabListRect.y,
        yEnd: tabListRect.y + tabListRect.height
    };
    if (blockDirection) {
        setTabListStartEndPosition(hostRef, tabListSizes.xStart, tabListSizes.xEnd - buttonRect.width);
    }
    else {
        setTabListStartEndPosition(hostRef, tabListSizes.yStart, tabListSizes.yEnd - buttonRect.height);
    }
    return tabListSizes;
};
const setMouseOffset = (element, offsetX, offsetY) => {
    setProperty(element, MOUSE_OFFSET_X, offsetX);
    setProperty(element, MOUSE_OFFSET_Y, offsetY);
};
const addGrabbingStyle = () => document.body.style.setProperty("cursor", "grabbing");
const removeGrabbingStyle = () => document.body.style.removeProperty("cursor");
const getNextEnabledButtonCaption = (currentBUttonCaption, tabListRef) => (currentBUttonCaption.nextElementSibling ??
    FIRST_CAPTION_BUTTON(tabListRef));
const getPreviousEnabledButtonCaption = (currentBUttonCaption, tabListRef) => (currentBUttonCaption.previousElementSibling ??
    LAST_CAPTION_BUTTON(tabListRef));
const focusNextOrPreviousCaption = (focusNextSibling, tabListRef, focusedCaption, event) => {
    // Prevent scroll
    event.preventDefault();
    let nextFocusedCaption;
    const searchFunction = focusNextSibling
        ? getNextEnabledButtonCaption
        : getPreviousEnabledButtonCaption;
    // Determine the next selected caption button
    nextFocusedCaption = searchFunction(focusedCaption, tabListRef);
    while (nextFocusedCaption.disabled) {
        nextFocusedCaption = searchFunction(nextFocusedCaption, tabListRef);
    }
    // Focus and select the caption
    nextFocusedCaption.focus();
    nextFocusedCaption.click();
};
let GET_IMAGE_PATH_CALLBACK_REGISTRY;
const DEFAULT_GET_IMAGE_PATH_CALLBACK = imageSrc => ({ base: imageSrc });
export class ChTabRender {
    constructor() {
        this.draggedElementIndex = -1;
        this.draggedElementNewIndex = -1;
        this.hasCrossedBoundaries = false;
        this.accessibleName = undefined;
        this.closeButton = false;
        this.closeButtonAccessibleName = "Close";
        this.contain = "none";
        this.disabled = false;
        this.dragOutside = false;
        this.expanded = true;
        this.getImagePathCallback = undefined;
        this.model = undefined;
        this.overflow = "visible";
        this.selectedId = undefined;
        this.showCaptions = true;
        this.showTabListEnd = false;
        this.showTabListStart = false;
        this.sortable = false;
        this.tabButtonHidden = false;
        this.tabListPosition = DEFAULT_TAB_LIST_POSITION;
    }
    #cancelId;
    #selectedIndex = -1;
    #lastDragEvent;
    #needForRAF = true; // To prevent redundant RAF (request animation frame) calls
    #initialMousePosition = -1;
    // Allocated at runtime to reduce memory usage
    #itemSizes;
    // TODO: Allocate at runtime to reduce memory usage
    #images = new Map();
    /**
     * This variable represents the boundaries of the box where the mouse can be
     * placed when dragging a caption, to consider that the caption is within the
     * tab list.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #mouseBoundingLimits;
    #renderedPages = new Map();
    #itemIdToIndex = new Map();
    // Refs
    #dragPreviewRef;
    #tabListRef;
    #tabPageRef;
    // Keyboard interactions
    #keyEvents = {
        [KEY_CODES.ARROW_UP]: (blockDirection, ev, focusedButton) => {
            if (blockDirection) {
                return;
            }
            focusNextOrPreviousCaption(false, this.#tabListRef, focusedButton, ev);
        },
        [KEY_CODES.ARROW_RIGHT]: (blockDirection, ev, focusedButton) => {
            if (!blockDirection) {
                return;
            }
            focusNextOrPreviousCaption(!isRTL(), this.#tabListRef, focusedButton, ev);
        },
        [KEY_CODES.ARROW_DOWN]: (blockDirection, ev, focusedButton) => {
            if (blockDirection) {
                return;
            }
            focusNextOrPreviousCaption(true, this.#tabListRef, focusedButton, ev);
        },
        [KEY_CODES.ARROW_LEFT]: (blockDirection, ev, focusedButton) => {
            if (!blockDirection) {
                return;
            }
            focusNextOrPreviousCaption(isRTL(), this.#tabListRef, focusedButton, ev);
        },
        [KEY_CODES.HOME]: (_, ev) => focusNextOrPreviousCaption(true, this.#tabListRef, LAST_CAPTION_BUTTON(this.#tabListRef), ev),
        [KEY_CODES.END]: (_, ev) => focusNextOrPreviousCaption(false, this.#tabListRef, FIRST_CAPTION_BUTTON(this.#tabListRef), ev)
    };
    el;
    getImagePathCallbackChanged() {
        this.#computeImages();
    }
    modelChanged(newModel) {
        this.#computeImages();
        this.#updateRenderedPages(newModel);
    }
    selectedIdChanged(newSelectedId) {
        const newSelectedTabItem = this.model.find(item => item.id === newSelectedId);
        if (newSelectedTabItem) {
            this.#renderedPages.set(newSelectedId, newSelectedTabItem);
        }
    }
    /**
     * Fired when an item of the main group is double clicked.
     */
    expandMainGroup;
    /**
     * Fired the close button of an item is clicked.
     */
    itemClose;
    /**
     * Fired when the selected item change.
     * This event can be default prevented to prevent the item selection.
     */
    selectedItemChange;
    /**
     * Fired the first time a caption button is dragged outside of its tab list.
     */
    itemDragStart;
    /**
     * Ends the preview of the dragged item. Useful for ending the preview via
     * keyboard interaction.
     */
    async endDragPreview() {
        this.#handleDragEnd();
    }
    /**
     * Returns the info associated to the draggable view.
     */
    async getDraggableViews() {
        return {
            mainView: this.el,
            pageView: this.#tabPageRef,
            tabListView: this.#tabListRef
        };
    }
    /**
     * Promotes the drag preview to the top layer. Useful to avoid z-index issues.
     */
    async promoteDragPreviewToTopLayer() {
        if (this.draggedElementIndex === -1) {
            return;
        }
        // If this property is added in a declarative way via the Stencil's render,
        // we would have to use requestAnimationFrame to delay the shopPopover()
        // method, since the popover defaults to "auto", which does not allow to
        // keep multiple "auto" popover open at the same time
        this.#dragPreviewRef.popover = "manual";
        this.#dragPreviewRef.showPopover();
    }
    /**
     * Given an id, remove the page from the render
     */
    async removePage(pageId, forceRerender = true) {
        this.#renderedPages.delete(pageId);
        if (forceRerender) {
            forceUpdate(this);
        }
    }
    // TODO: This code is exactly the same as the ch-accordion-render. We should
    // find the way to avoid this duplication
    #computeImage = (imageSrc) => {
        if (!imageSrc) {
            return undefined;
        }
        const getImagePathCallback = this.getImagePathCallback ?? GET_IMAGE_PATH_CALLBACK_REGISTRY;
        if (!getImagePathCallback) {
            return undefined;
        }
        const img = getImagePathCallback(imageSrc);
        return img
            ? updateDirectionInImageCustomVar(img, "start")
            : undefined;
    };
    #computeImages = () => {
        this.#images.clear();
        this.model?.forEach(itemUIModel => {
            const itemImage = this.#computeImage(itemUIModel.startImgSrc);
            if (itemImage) {
                this.#images.set(itemUIModel.id, itemImage);
            }
        });
    };
    #buttonIsCloseButton = (buttonRef) => buttonRef.className === CLOSE_BUTTON_CLASS;
    /**
     * Make a set based on the rendered items array to maintain order between the
     * pages, even when re-ordering tabs. This is useful for optimizing rendering
     * performance by not re-ordering pages when the caption's order changes.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #updateRenderedPages = (items) => {
        this.#renderedPages.clear();
        this.#itemIdToIndex.clear();
        (items ?? []).forEach(item => {
            if (item.wasRendered) {
                this.#renderedPages.set(item.id, item);
            }
        });
        // The selected id must be added to the rendered pages, even if it was not
        // marked as "wasRendered" in the UI Model
        if (this.selectedId && items !== undefined) {
            const newSelectedTabItem = this.model.find(item => item.id === this.selectedId);
            if (newSelectedTabItem) {
                this.#renderedPages.set(this.selectedId, newSelectedTabItem);
            }
        }
    };
    // #handleItemDblClick = (event: MouseEvent) => {
    //   event.preventDefault();
    //   event.stopPropagation();
    //   this.expandMainGroup.emit();
    // };
    #handleDragStart = (event) => {
        const buttonRef = event.composedPath()[0];
        if (buttonRef.tagName.toLowerCase() !== "button") {
            return;
        }
        // The only button that can perform dragstart is the tab button
        const itemIndex = this.#itemIdToIndex.get(buttonRef.id);
        // Remove dragover event to allow mousemove event to fire
        event.preventDefault();
        // Store the index of the dragged element
        this.draggedElementIndex = itemIndex;
        // - - - - - - - - - - - DOM read operations - - - - - - - - - - -
        const mousePositionX = event.clientX;
        const mousePositionY = event.clientY;
        const blockDirection = isBlockDirection(this.tabListPosition);
        const getItemSize = blockDirection
            ? (item) => item.getBoundingClientRect().width
            : (item) => item.getBoundingClientRect().height;
        this.#itemSizes = [...this.#tabListRef.children].map(getItemSize);
        const buttonRect = event.target.getBoundingClientRect();
        // Tab List information
        const tabListSizes = getTabListSizesAndSetPosition(this.el, this.#tabListRef, blockDirection, buttonRect);
        // Button information
        const buttonSizes = {
            xStart: buttonRect.x,
            xEnd: buttonRect.x + buttonRect.width,
            yStart: buttonRect.y,
            yEnd: buttonRect.y + buttonRect.height
        };
        const mouseDistanceToButtonTopEdge = mousePositionY - buttonSizes.yStart;
        const mouseDistanceToButtonBottomEdge = buttonSizes.yEnd - mousePositionY;
        const mouseDistanceToButtonLeftEdge = mousePositionX - buttonSizes.xStart;
        const mouseDistanceToButtonRightEdge = buttonSizes.xEnd - mousePositionX;
        // Update mouse limits if drag outside is enabled
        if (this.dragOutside) {
            this.#mouseBoundingLimits = {
                xStart: tabListSizes.xStart - mouseDistanceToButtonRightEdge,
                xEnd: tabListSizes.xEnd + mouseDistanceToButtonLeftEdge,
                yStart: tabListSizes.yStart - mouseDistanceToButtonBottomEdge,
                yEnd: tabListSizes.yEnd + mouseDistanceToButtonTopEdge
            };
        }
        // Store initial mouse position
        this.#initialMousePosition = blockDirection
            ? mousePositionX
            : mousePositionY;
        // - - - - - - - - - - - DOM write operations - - - - - - - - - - -
        // Initialize mouse position to avoid initial flickering
        setMousePosition(this.el, mousePositionX, mousePositionY);
        // Initialize the button position
        setButtonInitialPosition(this.el, buttonSizes.xStart, buttonSizes.yStart);
        setButtonSize(this.el, blockDirection ? buttonRect.width : buttonRect.height);
        // Update mouse offset to correctly place the dragged element preview
        setMouseOffset(this.el, mouseDistanceToButtonLeftEdge, mouseDistanceToButtonTopEdge);
        addGrabbingStyle();
        // Add listeners
        document.addEventListener("mousemove", this.#handleItemDrag, {
            capture: true,
            passive: true
        });
        document.addEventListener("mouseup", this.#handleDragEnd, {
            capture: true
        });
    };
    #handleDragEnd = () => {
        // Since mousemove callbacks are executed on animation frames, we must also
        // remove the events on animations frame. Otherwise we would remove the
        // events and in the next frame the mousemove handler will be executes
        cancelAnimationFrame(this.#cancelId);
        this.#needForRAF = true;
        document.removeEventListener("mousemove", this.#handleItemDrag, {
            capture: true
        });
        document.removeEventListener("mouseup", this.#handleDragEnd, {
            capture: true
        });
        removeGrabbingStyle();
        const anItemWasReordered = !this.hasCrossedBoundaries &&
            this.draggedElementNewIndex !== this.draggedElementIndex;
        // Move the item to the new position
        if (anItemWasReordered) {
            const itemToInsert = removeElement(this.model, this.draggedElementIndex);
            insertIntoIndex(this.model, itemToInsert, this.draggedElementNewIndex);
            // Update last selected index
            this.#adjustLastSelectedIndexValueAfterReorder();
        }
        // Restore visibility of the dragged element
        this.draggedElementIndex = -1;
        this.draggedElementNewIndex = -1;
        // Free the memory
        this.#itemSizes = undefined;
        // Reset state
        this.hasCrossedBoundaries = false;
        this.el.style.removeProperty(TRANSITION_DURATION);
    };
    #adjustLastSelectedIndexValueAfterReorder = () => {
        // If the dragged element is the selected element, use the new index
        if (this.#selectedIndex === this.draggedElementIndex) {
            this.#selectedIndex = this.draggedElementNewIndex;
        }
        // Dragged element:
        //   - Started: Before the selected index
        //   - Ended: After the selected index or in the same position
        else if (this.draggedElementIndex < this.#selectedIndex &&
            this.#selectedIndex <= this.draggedElementNewIndex) {
            this.#selectedIndex--;
        }
        // Dragged element:
        //   - Started: After the selected index
        //   - Ended: Before the selected index or in the same position
        else if (this.#selectedIndex < this.draggedElementIndex &&
            this.draggedElementNewIndex <= this.#selectedIndex) {
            this.#selectedIndex++;
        }
    };
    #handleItemDrag = (event) => {
        this.#lastDragEvent = event;
        if (!this.#needForRAF) {
            return;
        }
        this.#needForRAF = false; // No need to call RAF up until next frame
        this.#cancelId = requestAnimationFrame(() => {
            this.#needForRAF = true; // RAF now consumes the movement instruction so a new one can come
            const mousePositionX = this.#lastDragEvent.clientX;
            const mousePositionY = this.#lastDragEvent.clientY;
            setMousePosition(this.el, mousePositionX, mousePositionY);
            // There is no need to update the preview of the reorder
            if (this.hasCrossedBoundaries) {
                return;
            }
            const mouseLimits = this.#mouseBoundingLimits;
            // Check mouse limits if drag outside is enabled
            if (this.dragOutside) {
                const draggedButtonIsInsideTheTabList = inBetween(mouseLimits.xStart, mousePositionX, mouseLimits.xEnd) &&
                    inBetween(mouseLimits.yStart, mousePositionY, mouseLimits.yEnd);
                // Emit the itemDragStart event the first time the button is out of the
                // mouse bounds (`mouseBoundingLimits`)
                if (!draggedButtonIsInsideTheTabList) {
                    this.hasCrossedBoundaries = true;
                    // Remove transition before the render to avoid flickering in the animation
                    this.el.style.setProperty(TRANSITION_DURATION, "0s");
                    this.itemDragStart.emit(this.draggedElementIndex);
                    return;
                }
            }
            // There is no need to re-order the items in the preview
            if (this.model.length === 1) {
                return;
            }
            // In this point, the preview is inside the tab list, we should check
            // in which place is the preview to give feedback for the item's reorder
            const mousePosition = isBlockDirection(this.tabListPosition)
                ? mousePositionX
                : mousePositionY;
            const hasMovedToTheEnd = this.#initialMousePosition < mousePosition;
            // Distance traveled from the initial mouse position
            let distanceTraveled = Math.abs(this.#initialMousePosition - mousePosition);
            let newIndex = this.draggedElementIndex;
            if (hasMovedToTheEnd) {
                // While it is not the last item and the distance traveled is greater
                // than half the size of the next item
                while (newIndex < this.model.length - 1 &&
                    distanceTraveled - this.#itemSizes[newIndex + 1] / 2 > 0) {
                    distanceTraveled -= this.#itemSizes[newIndex + 1];
                    newIndex++;
                }
            }
            else {
                // While it is not the first item and the distance traveled is greater
                // than half the size of the previous item
                while (newIndex > 0 &&
                    distanceTraveled - this.#itemSizes[newIndex - 1] / 2 > 0) {
                    distanceTraveled -= this.#itemSizes[newIndex - 1];
                    newIndex--;
                }
            }
            // Check if should update the dragged element index in the preview
            if (this.draggedElementNewIndex !== newIndex) {
                this.draggedElementNewIndex = newIndex;
            }
        });
    };
    #preventMouseDownOnScroll = (event) => {
        // We have to prevent the mousedown event to make work the close with the
        // mouse wheel, because when the page has scroll, the auxClick is not fired
        if (event.buttons !== MouseEventButtons.WHEEL) {
            return;
        }
        const buttonRef = event.composedPath()[0];
        if (buttonRef.tagName.toLowerCase() !== "button") {
            return;
        }
        event.preventDefault();
    };
    #handleSelectedItemChange = (event) => {
        event.stopPropagation();
        const composedPath = event.composedPath();
        const buttonRef = composedPath.find(eventTarget => eventTarget.tagName?.toLowerCase() === "button");
        // Check the click event is performed on a button element
        if (!buttonRef || buttonRef.getRootNode() !== this.el.shadowRoot) {
            return;
        }
        // Click was performed on the close button --> itemClose event
        if (this.#buttonIsCloseButton(buttonRef)) {
            // The parent is the tab button with the itemId
            this.#emitCloseEvent(buttonRef.parentElement.id, event);
        }
        // Otherwise --> selectedItemChange event
        else {
            const itemId = buttonRef.id;
            // Don't fire the selectedItemChange event if the item is already selected
            if (this.selectedId === itemId) {
                return;
            }
            const itemIndex = this.#itemIdToIndex.get(itemId);
            const eventInfo = this.selectedItemChange.emit({
                lastSelectedIndex: this.#selectedIndex,
                newSelectedId: itemId,
                newSelectedIndex: itemIndex
            });
            if (!eventInfo.defaultPrevented) {
                this.#selectedIndex = itemIndex;
                this.selectedId = itemId;
            }
        }
    };
    #handleClose = (event) => {
        // Check if the action was performed on the close button or the action
        // was a click on the wheel
        if (event.button !== MouseEventButton.WHEEL) {
            return;
        }
        const buttonRef = event.composedPath()[0];
        if (buttonRef.tagName.toLowerCase() === "button") {
            const itemId = this.#buttonIsCloseButton(buttonRef)
                ? buttonRef.parentElement.id
                : buttonRef.id;
            this.#emitCloseEvent(itemId, event);
        }
    };
    #emitCloseEvent = (itemId, event) => {
        // Assume that the itemId always maps to an item
        const itemUIModel = this.model.find(({ id }) => id === itemId);
        const hasCloseButton = itemUIModel.closeButton ?? this.closeButton;
        if (!hasCloseButton) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        const itemIndex = this.#itemIdToIndex.get(itemId);
        this.itemClose.emit({
            itemIndex: itemIndex,
            itemId: itemId
        });
    };
    #handleTabFocus = (event) => {
        const keyEventHandler = this.#keyEvents[event.code];
        if (!keyEventHandler) {
            return;
        }
        const currentFocusedCaption = focusComposedPath()[0].closest("." + TAB_BUTTON_CLASS);
        keyEventHandler(isBlockDirection(this.tabListPosition), event, currentFocusedCaption);
    };
    #getEnabledItems = () => {
        let itemsEnabled = 0;
        let itemIndex = 0;
        while (itemsEnabled < 2 && itemIndex < this.model.length) {
            const itemUIModel = this.model[itemIndex];
            if (itemUIModel.disabled === false ||
                (!this.disabled && itemUIModel.disabled !== true)) {
                itemsEnabled++;
            }
            itemIndex++;
        }
        return itemsEnabled;
    };
    #imgRender = (item) => item.startImgType === "img" &&
        item.startImgSrc && (h("img", { "aria-hidden": "true", class: "caption-image img", part: TAB_PARTS_DICTIONARY.IMAGE, alt: "", src: item.startImgSrc, loading: "lazy" }));
    #renderTabListPosition = (position, blockDirection, startDirection) => {
        const tabListPosition = `tab-list-${position}`;
        return (h("div", { key: tabListPosition, class: tabListPosition, part: tokenMap({
                [TAB_PARTS_DICTIONARY.LIST_START]: position === "start",
                [TAB_PARTS_DICTIONARY.LIST_END]: position === "end",
                [this.tabListPosition]: true,
                [TAB_PARTS_DICTIONARY.BLOCK]: blockDirection,
                [TAB_PARTS_DICTIONARY.INLINE]: !blockDirection,
                [TAB_PARTS_DICTIONARY.START]: startDirection,
                [TAB_PARTS_DICTIONARY.END]: !startDirection
            }) }, h("slot", { name: tabListPosition })));
    };
    #renderTabList = (thereAreShiftedElements) => {
        const blockDirection = isBlockDirection(this.tabListPosition);
        const startDirection = isStartDirection(this.tabListPosition);
        const enabledItems = this.#getEnabledItems();
        const atLeastOneItemsIsEnabled = enabledItems >= 1;
        return (h("div", { role: "tablist", "aria-label": this.accessibleName, class: {
                "tab-list": true,
                "tab-list--block": blockDirection,
                "tab-list--inline": !blockDirection
            },
            // TODO: Add "DRAGGING" parts
            part: tokenMap({
                [TAB_PARTS_DICTIONARY.LIST]: true,
                [this.tabListPosition]: true,
                [TAB_PARTS_DICTIONARY.BLOCK]: blockDirection,
                [TAB_PARTS_DICTIONARY.INLINE]: !blockDirection,
                [TAB_PARTS_DICTIONARY.START]: startDirection,
                [TAB_PARTS_DICTIONARY.END]: !startDirection
            }),
            // TODO: Don't add this handler if there is no items with closeButton
            onAuxClick: atLeastOneItemsIsEnabled ? this.#handleClose : undefined, onClick: atLeastOneItemsIsEnabled ? this.#handleSelectedItemChange : undefined,
            // TODO: Add support to drag the item when it is disabled.
            // TODO: Add support to position the item in different areas
            onDragStart: this.sortable && atLeastOneItemsIsEnabled
                ? this.#handleDragStart
                : undefined, onKeyDown: this.model.length >= 2 && enabledItems >= 2
                ? this.#handleTabFocus
                : undefined,
            // TODO: Don't add this handler if there is no items with closeButton
            onMouseDown: atLeastOneItemsIsEnabled ? this.#preventMouseDownOnScroll : undefined, ref: el => (this.#tabListRef = el) }, this.model.map((item, index) => this.#renderTabButton(item, index, thereAreShiftedElements, blockDirection, startDirection))));
    };
    #renderTabButton = (item, index, thereAreShiftedElements, blockDirection, startDirection) => {
        const closeButton = item.closeButton ?? this.closeButton;
        const isDisabled = item.disabled ?? this.disabled;
        const selected = item.id === this.selectedId;
        this.#itemIdToIndex.set(item.id, index);
        const startImage = this.#images.get(item.id);
        const startImageClasses = startImage?.classes;
        const isDecorativeImage = isPseudoElementImg(item.startImgSrc, item.startImgType) && !!startImage;
        return (h("button", { key: item.id, id: item.id, role: "tab", "aria-controls": PANEL_ID(item.id), "aria-label": item.accessibleName ?? (!this.showCaptions ? item.name : null), "aria-selected": selected.toString(), class: {
                [TAB_BUTTON_CLASS]: true,
                "no-captions": !this.showCaptions,
                sortable: this.sortable,
                selected: selected,
                [`start-img-type--${item.startImgType ?? "background"} pseudo-img--start`]: isDecorativeImage,
                [startImageClasses]: isDecorativeImage && !!startImageClasses,
                "dragged-element": this.draggedElementIndex === index,
                "dragged-element--outside": this.draggedElementIndex === index &&
                    this.hasCrossedBoundaries &&
                    this.model.length > 1,
                "shifted-element": this.draggedElementIndex !== -1,
                "shifted-element--start": thereAreShiftedElements &&
                    this.draggedElementIndex < index &&
                    index <= this.draggedElementNewIndex,
                "shifted-element--end": thereAreShiftedElements &&
                    this.draggedElementNewIndex <= index &&
                    index < this.draggedElementIndex
            }, part: this.#getTabParts({
                id: item.id,
                blockDirection,
                closeButton,
                isDisabled,
                isTabCaption: false,
                selected,
                startDirection
            }), disabled: isDisabled, style: isDecorativeImage ? startImage.styles : undefined }, this.#imgRender(item), this.showCaptions && (h("ch-textblock", {
            // TODO: Add a unit test that clicks this element an verifies that
            // the tab selection did change
            class: "tab-caption", part: this.#getTabParts({
                id: item.id,
                blockDirection,
                closeButton,
                isDisabled,
                isTabCaption: true,
                selected,
                startDirection
            }), caption: item.name, showTooltipOnOverflow: true
        })), closeButton && (h("button", { "aria-label": this.closeButtonAccessibleName, class: CLOSE_BUTTON_CLASS, part: tokenMap({
                [TAB_PARTS_DICTIONARY.CLOSE_BUTTON]: true,
                [this.tabListPosition]: true,
                [TAB_PARTS_DICTIONARY.BLOCK]: blockDirection,
                [TAB_PARTS_DICTIONARY.INLINE]: !blockDirection,
                [TAB_PARTS_DICTIONARY.START]: startDirection,
                [TAB_PARTS_DICTIONARY.END]: !startDirection,
                [TAB_PARTS_DICTIONARY.SELECTED]: selected,
                [TAB_PARTS_DICTIONARY.NOT_SELECTED]: !selected,
                [TAB_PARTS_DICTIONARY.DISABLED]: isDisabled
            }), disabled: isDisabled, type: "button" }))));
    };
    #getTabParts = ({ id, blockDirection, closeButton, isDisabled, isTabCaption, selected, startDirection }) => tokenMap({
        [id]: true,
        [TAB_PARTS_DICTIONARY.TAB]: !isTabCaption,
        [TAB_PARTS_DICTIONARY.TAB_CAPTION]: isTabCaption,
        [this.tabListPosition]: true,
        [TAB_PARTS_DICTIONARY.BLOCK]: blockDirection,
        [TAB_PARTS_DICTIONARY.INLINE]: !blockDirection,
        [TAB_PARTS_DICTIONARY.START]: startDirection,
        [TAB_PARTS_DICTIONARY.END]: !startDirection,
        [TAB_PARTS_DICTIONARY.CLOSABLE]: closeButton,
        [TAB_PARTS_DICTIONARY.NOT_CLOSABLE]: !closeButton,
        [TAB_PARTS_DICTIONARY.SELECTED]: selected,
        [TAB_PARTS_DICTIONARY.NOT_SELECTED]: !selected,
        [TAB_PARTS_DICTIONARY.DISABLED]: isDisabled
    });
    #renderTabPages = (blockDirection, startDirection) => (h("div", {
        // This key is used key toggling the tabButtonHidden property, so we
        // avoid destroying this div
        key: "panel-container", class: {
            "panel-container": true,
            "panel-container--collapsed": !this.expanded
        }, part: tokenMap({
            [TAB_PARTS_DICTIONARY.PANEL_CONTAINER]: true,
            [this.tabListPosition]: true,
            [TAB_PARTS_DICTIONARY.BLOCK]: blockDirection,
            [TAB_PARTS_DICTIONARY.INLINE]: !blockDirection,
            [TAB_PARTS_DICTIONARY.START]: startDirection,
            [TAB_PARTS_DICTIONARY.END]: !startDirection
        }), ref: el => (this.#tabPageRef = el)
    }, [...this.#renderedPages.values()].map(this.#renderTabPanel)));
    #renderTabPanel = (item) => {
        // TODO: Avoid this check as much as possible
        const blockDirection = isBlockDirection(this.tabListPosition);
        const startDirection = isStartDirection(this.tabListPosition);
        const contain = item.contain ?? this.contain;
        const isDisabled = item.disabled ?? this.disabled;
        const overflow = item.overflow ?? this.overflow;
        const selected = item.id === this.selectedId;
        const hasContain = contain !== "none";
        const hasOverflow = overflow !== "visible" && overflow !== "visible visible";
        return (h("div", { key: PANEL_ID(item.id), id: PANEL_ID(item.id), role: !this.tabButtonHidden ? "tabpanel" : undefined, "aria-labelledby": !this.tabButtonHidden ? item.id : undefined, class: {
                panel: true,
                "panel--selected": item.id === this.selectedId,
                "panel--hidden": !(item.id === this.selectedId),
                [SCROLLABLE_CLASS]: hasOverflow &&
                    (overflow.includes("auto") ||
                        overflow.includes("scroll"))
            }, style: hasContain || hasOverflow
                ? {
                    contain: hasContain ? contain : undefined,
                    overflow: hasOverflow ? overflow : undefined
                }
                : undefined, part: tokenMap({
                [item.id]: true,
                [TAB_PARTS_DICTIONARY.PANEL]: true,
                [this.tabListPosition]: true,
                [TAB_PARTS_DICTIONARY.BLOCK]: blockDirection,
                [TAB_PARTS_DICTIONARY.INLINE]: !blockDirection,
                [TAB_PARTS_DICTIONARY.START]: startDirection,
                [TAB_PARTS_DICTIONARY.END]: !startDirection,
                [TAB_PARTS_DICTIONARY.SELECTED]: selected,
                [TAB_PARTS_DICTIONARY.NOT_SELECTED]: !selected,
                [TAB_PARTS_DICTIONARY.DISABLED]: isDisabled
            }) }, h("slot", { name: item.id })));
    };
    #renderDragPreview = (draggedElement) => {
        const blockDirection = isBlockDirection(this.tabListPosition);
        const startDirection = isStartDirection(this.tabListPosition);
        const selected = draggedElement.id === this.selectedId;
        const startImage = this.#images.get(draggedElement.id);
        const startImageClasses = startImage?.classes;
        const isDecorativeImage = isPseudoElementImg(draggedElement.startImgSrc, draggedElement.startImgType) && !!startImage;
        const closeButton = draggedElement.closeButton ?? this.closeButton;
        return (h("button", {
            // TODO: Check if this is necessary
            // aria-hidden="true"
            class: {
                [TAB_BUTTON_CLASS]: true,
                [DRAG_PREVIEW]: true,
                "no-captions": !this.showCaptions,
                selected: selected,
                [`start-img-type--${draggedElement.startImgType ?? "background"} pseudo-img--start`]: isDecorativeImage,
                [startImageClasses]: isDecorativeImage && !!startImageClasses,
                [DRAG_PREVIEW_OUTSIDE]: this.hasCrossedBoundaries,
                [DRAG_PREVIEW_INSIDE_INLINE]: !this.hasCrossedBoundaries && !blockDirection,
                [DRAG_PREVIEW_INSIDE_BLOCK]: !this.hasCrossedBoundaries && blockDirection
            }, part: tokenMap({
                [draggedElement.id]: true,
                [TAB_PARTS_DICTIONARY.TAB]: true,
                [TAB_PARTS_DICTIONARY.DRAGGING]: true,
                [this.tabListPosition]: true,
                [TAB_PARTS_DICTIONARY.BLOCK]: blockDirection,
                [TAB_PARTS_DICTIONARY.INLINE]: !blockDirection,
                [TAB_PARTS_DICTIONARY.START]: startDirection,
                [TAB_PARTS_DICTIONARY.END]: !startDirection,
                [TAB_PARTS_DICTIONARY.DRAGGING_OUT_OF_TAB_LIST]: this.hasCrossedBoundaries,
                [TAB_PARTS_DICTIONARY.DRAGGING_OVER_TAB_LIST]: !this.hasCrossedBoundaries,
                [TAB_PARTS_DICTIONARY.CLOSABLE]: closeButton,
                [TAB_PARTS_DICTIONARY.NOT_CLOSABLE]: !closeButton,
                [TAB_PARTS_DICTIONARY.SELECTED]: selected,
                [TAB_PARTS_DICTIONARY.NOT_SELECTED]: !selected
            }), style: isDecorativeImage ? startImage.styles : undefined, ref: el => (this.#dragPreviewRef = el)
        }, this.#imgRender(draggedElement), this.showCaptions && draggedElement.name));
    };
    #initializeState = () => {
        this.#updateRenderedPages(this.model);
    };
    connectedCallback() {
        adoptCommonThemes(this.el.shadowRoot.adoptedStyleSheets);
        // Initialize default getImagePathCallback
        GET_IMAGE_PATH_CALLBACK_REGISTRY ??=
            getControlRegisterProperty("getImagePathCallback", "ch-tab-render") ??
                DEFAULT_GET_IMAGE_PATH_CALLBACK;
        this.#computeImages();
    }
    // TODO: Use connectedCallback
    componentWillLoad() {
        this.#initializeState();
    }
    render() {
        if (this.model == null || this.model.length === 0) {
            return "";
        }
        const blockDirection = isBlockDirection(this.tabListPosition);
        const startDirection = isStartDirection(this.tabListPosition);
        const draggedIndex = this.draggedElementIndex;
        const draggedElement = this.model[draggedIndex];
        const thereAreShiftedElementsInPreview = !this.hasCrossedBoundaries &&
            this.draggedElementNewIndex !== -1 &&
            this.draggedElementIndex !== this.draggedElementNewIndex;
        return (h(Host, { class: 
            // TODO: Add a unit test for the tabButtonHidden property
            !this.tabButtonHidden ? `ch-tab--${this.tabListPosition}` : undefined }, !this.tabButtonHidden && [
            this.showTabListStart &&
                this.#renderTabListPosition("start", blockDirection, startDirection),
            this.#renderTabList(thereAreShiftedElementsInPreview),
            this.showTabListEnd &&
                this.#renderTabListPosition("end", blockDirection, startDirection)
        ], this.#renderTabPages(blockDirection, startDirection), draggedIndex !== -1 && this.#renderDragPreview(draggedElement)));
    }
    static get is() { return "ch-tab-render"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["tab.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["tab.css"]
        };
    }
    static get properties() {
        return {
            "accessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor the element."
                },
                "attribute": "accessible-name",
                "reflect": false
            },
            "closeButton": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to display a close button for the items."
                },
                "attribute": "close-button",
                "reflect": false,
                "defaultValue": "false"
            },
            "closeButtonAccessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor the element. This label is used for the close button of the captions."
                },
                "attribute": "close-button-accessible-name",
                "reflect": false,
                "defaultValue": "\"Close\""
            },
            "contain": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "CssContainProperty",
                    "resolved": "\"content\" | \"inline-size\" | \"layout\" | \"none\" | \"paint\" | \"size\" | \"strict\" | \"style\"",
                    "references": {
                        "CssContainProperty": {
                            "location": "import",
                            "path": "../../common/types",
                            "id": "src/common/types.ts::CssContainProperty"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Same as the contain CSS property. This property indicates that an item\nand its contents are, as much as possible, independent from the rest of\nthe document tree. Containment enables isolating a subsection of the DOM,\nproviding performance benefits by limiting calculations of layout, style,\npaint, size, or any combination to a DOM subtree rather than the entire\npage.\nContainment can also be used to scope CSS counters and quotes."
                },
                "attribute": "contain",
                "reflect": false,
                "defaultValue": "\"none\""
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if all tab buttons are disabled.\nIf disabled, tab buttons will not fire any user interaction related event\n(for example, click event)."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "dragOutside": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "When the control is sortable, the items can be dragged outside of the\ntab-list.\n\nThis property lets you specify if this behavior is enabled."
                },
                "attribute": "drag-outside",
                "reflect": false,
                "defaultValue": "false"
            },
            "expanded": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` if the group has is view section expanded. Otherwise, only the\ntoolbar will be displayed."
                },
                "attribute": "expanded",
                "reflect": false,
                "defaultValue": "true"
            },
            "getImagePathCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    imageSrc: string\n  ) => GxImageMultiState | undefined",
                    "resolved": "(imageSrc: string) => GxImageMultiState",
                    "references": {
                        "GxImageMultiState": {
                            "location": "import",
                            "path": "../../common/types",
                            "id": "src/common/types.ts::GxImageMultiState"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property specifies a callback that is executed when the path for an\nstartImgSrc needs to be resolved."
                }
            },
            "model": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "TabModel",
                    "resolved": "TabItemModel[]",
                    "references": {
                        "TabModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/tab/types.ts::TabModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the items of the tab control."
                }
            },
            "overflow": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "| CssOverflowProperty\n    | `${CssOverflowProperty} ${CssOverflowProperty}`",
                    "resolved": "CssOverflowProperty | \"auto auto\" | \"auto hidden\" | \"auto clip\" | \"auto scroll\" | \"auto visible\" | \"hidden auto\" | \"hidden hidden\" | \"hidden clip\" | \"hidden scroll\" | \"hidden visible\" | \"clip auto\" | \"clip hidden\" | \"clip clip\" | \"clip scroll\" | \"clip visible\" | \"scroll auto\" | \"scroll hidden\" | \"scroll clip\" | \"scroll scroll\" | \"scroll visible\" | \"visible auto\" | \"visible hidden\" | \"visible clip\" | \"visible scroll\" | \"visible visible\"",
                    "references": {
                        "CssOverflowProperty": {
                            "location": "import",
                            "path": "../../common/types",
                            "id": "src/common/types.ts::CssOverflowProperty"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Same as the overflow CSS property. This property sets the desired behavior\nwhen content does not fit in the item's padding box (overflows) in the\nhorizontal and/or vertical direction."
                },
                "attribute": "overflow",
                "reflect": false,
                "defaultValue": "\"visible\""
            },
            "selectedId": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the selected item of the widgets array."
                },
                "attribute": "selected-id",
                "reflect": false
            },
            "showCaptions": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to show the captions of the items."
                },
                "attribute": "show-captions",
                "reflect": false,
                "defaultValue": "true"
            },
            "showTabListEnd": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to render a slot named \"tab-list-end\" to project content at the\nend position of the tab-list (\"after\" the tab buttons)."
                },
                "attribute": "show-tab-list-end",
                "reflect": false,
                "defaultValue": "false"
            },
            "showTabListStart": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to render a slot named \"tab-list-start\" to project content at the\nstart position of the tab-list (\"before\" the tab buttons)."
                },
                "attribute": "show-tab-list-start",
                "reflect": false,
                "defaultValue": "false"
            },
            "sortable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to enable sorting the tab buttons by dragging them in the tab-list.\n\nIf `false`, the tab buttons can not be dragged out either."
                },
                "attribute": "sortable",
                "reflect": false,
                "defaultValue": "false"
            },
            "tabButtonHidden": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to not render the tab buttons of the control."
                },
                "attribute": "tab-button-hidden",
                "reflect": false,
                "defaultValue": "false"
            },
            "tabListPosition": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "TabListPosition",
                    "resolved": "\"block-end\" | \"block-start\" | \"inline-end\" | \"inline-start\"",
                    "references": {
                        "TabListPosition": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/tab/types.ts::TabListPosition"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the position of the tab list of the `ch-tab-render`."
                },
                "attribute": "tab-list-position",
                "reflect": false,
                "defaultValue": "DEFAULT_TAB_LIST_POSITION"
            }
        };
    }
    static get states() {
        return {
            "draggedElementIndex": {},
            "draggedElementNewIndex": {},
            "hasCrossedBoundaries": {}
        };
    }
    static get events() {
        return [{
                "method": "expandMainGroup",
                "name": "expandMainGroup",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when an item of the main group is double clicked."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }, {
                "method": "itemClose",
                "name": "itemClose",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired the close button of an item is clicked."
                },
                "complexType": {
                    "original": "TabItemCloseInfo",
                    "resolved": "{ itemId: string; itemIndex: number; }",
                    "references": {
                        "TabItemCloseInfo": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/tab/types.ts::TabItemCloseInfo"
                        }
                    }
                }
            }, {
                "method": "selectedItemChange",
                "name": "selectedItemChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the selected item change.\nThis event can be default prevented to prevent the item selection."
                },
                "complexType": {
                    "original": "TabSelectedItemInfo",
                    "resolved": "{ lastSelectedIndex: number; newSelectedId: string; newSelectedIndex: number; }",
                    "references": {
                        "TabSelectedItemInfo": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/tab/types.ts::TabSelectedItemInfo"
                        }
                    }
                }
            }, {
                "method": "itemDragStart",
                "name": "itemDragStart",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired the first time a caption button is dragged outside of its tab list."
                },
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "endDragPreview": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Ends the preview of the dragged item. Useful for ending the preview via\nkeyboard interaction.",
                    "tags": []
                }
            },
            "getDraggableViews": {
                "complexType": {
                    "signature": "() => Promise<DraggableViewInfo>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "DraggableViewInfo": {
                            "location": "import",
                            "path": "../flexible-layout/internal/flexible-layout/types",
                            "id": "src/components/flexible-layout/internal/flexible-layout/types.ts::DraggableViewInfo"
                        }
                    },
                    "return": "Promise<DraggableViewInfo>"
                },
                "docs": {
                    "text": "Returns the info associated to the draggable view.",
                    "tags": []
                }
            },
            "promoteDragPreviewToTopLayer": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Promotes the drag preview to the top layer. Useful to avoid z-index issues.",
                    "tags": []
                }
            },
            "removePage": {
                "complexType": {
                    "signature": "(pageId: string, forceRerender?: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "pageId",
                            "type": "string",
                            "docs": ""
                        }, {
                            "name": "forceRerender",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Given an id, remove the page from the render",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "getImagePathCallback",
                "methodName": "getImagePathCallbackChanged"
            }, {
                "propName": "model",
                "methodName": "modelChanged"
            }, {
                "propName": "selectedId",
                "methodName": "selectedIdChanged"
            }];
    }
}
